// ✅ src/components/screens/PublicProductScreen.js (FULL - FIXED)
// ✅ FIX: Old price now matches the price you entered (e.g. 300), not random 500 fields
// ✅ FIX: "aqua" now shows aqua (not red) + many more named colors supported
// ✅ Supports ANY color string via stable hash -> HSL fallback
// ✅ Still: per-store cart, single cart line per product, viewer, mobile size sheet, breadcrumbs, etc.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { db } from "../../lib/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";

function safe(v) {
  return String(v ?? "").trim();
}

function money(n, cur = "GHS") {
  const num = Number(n);
  if (Number.isNaN(num)) return `${cur} —`;
  return `${cur} ${num.toLocaleString()}`;
}

function getPriceValue(p) {
  const candidates = [
    p?.price,
    p?.amount,
    p?.unitPrice,
    p?.priceGHS,
    p?.priceValue,
    p?.salePrice,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (!Number.isNaN(n) && n > 0) return n;
  }
  const zero = Number(p?.price);
  if (!Number.isNaN(zero)) return zero;
  return null;
}

/* ---------------- DISCOUNT (old price = your price) ---------------- */
function applyDiscount(priceVal, discountPercent) {
  const p = Number(priceVal);
  const d = Number(discountPercent || 0);
  if (!Number.isFinite(p)) return null;
  if (!Number.isFinite(d) || d <= 0) return p;

  const clamped = Math.max(0, Math.min(100, d));
  const raw = p * (1 - clamped / 100);

  const final = Math.round(raw);
  return Number.isFinite(final) ? final : p;
}

/* ---------------- PER-STORE Cart ---------------- */
function cartKey(storeId) {
  return `cart_items_v1:${String(storeId || "unknown")}`;
}
function loadCart(storeId) {
  try {
    const raw = localStorage.getItem(cartKey(storeId));
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}
function saveCart(storeId, items) {
  try {
    localStorage.setItem(cartKey(storeId), JSON.stringify(items || []));
  } catch {}
}
function getQtyInCart(items, productId) {
  const pid = String(productId || "");
  const it = (items || []).find((x) => String(x?.productId) === pid);
  return Number(it?.qty || 0);
}

/* ---------------- Options helpers ---------------- */
function normalizeList(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v.map((x) => safe(x)).filter(Boolean);
  if (typeof v === "string") {
    return v
      .split(/[,|/\n]+/g)
      .map((x) => safe(x))
      .filter(Boolean);
  }
  return [];
}

function expandRangeToken(token) {
  const t = safe(token);
  const m = t.match(/^(\d+)\s*-\s*(\d+)$/);
  if (!m) return null;

  const a = Number(m[1]);
  const b = Number(m[2]);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;

  const start = Math.min(a, b);
  const end = Math.max(a, b);

  const maxCount = 200;
  if (end - start + 1 > maxCount) return [t];

  const out = [];
  for (let i = start; i <= end; i++) out.push(String(i));
  return out;
}

function splitOptions(v) {
  const base = normalizeList(v);
  const out = [];
  for (const p of base) {
    const expanded = expandRangeToken(p);
    if (expanded) out.push(...expanded);
    else out.push(p);
  }

  const seen = new Set();
  const final = [];
  for (const item of out) {
    const key = safe(item).toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    final.push(item);
  }
  return final;
}

/* ---------------- Case-insensitive attribute helpers ---------------- */
function getAttrCI(attrs, keys = []) {
  if (!attrs || typeof attrs !== "object") return "";
  const map = new Map(
    Object.entries(attrs).map(([k, v]) => [safe(k).toLowerCase(), v])
  );
  for (const k of keys) {
    const v = map.get(safe(k).toLowerCase());
    if (safe(v)) return v;
  }
  return "";
}
function getAttrOptionsCI(attrs, keys = []) {
  const v = getAttrCI(attrs, keys);
  return splitOptions(v);
}

function getAttrOptionsFromProduct(p, keys = []) {
  const attrs = p?.attributes;
  if (!attrs || typeof attrs !== "object") return [];
  return getAttrOptionsCI(attrs, keys);
}

function getSizesFromProduct(p) {
  const out = [
    ...splitOptions(p?.sizes),
    ...splitOptions(p?.size),
    ...splitOptions(p?.sizeOptions),
    ...splitOptions(p?.availableSizes),
    ...splitOptions(p?.variants?.sizes),
    ...splitOptions(p?.variants?.size),
    ...splitOptions(p?.options?.sizes),
    ...splitOptions(p?.options?.size),
    ...getAttrOptionsFromProduct(p, ["size", "sizes", "Size", "Sizes"]),
  ];

  const seen = new Set();
  const final = [];
  for (const item of out) {
    const k = safe(item).toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    final.push(item);
  }
  return final;
}

function getColorsFromProduct(p) {
  const out = [
    ...splitOptions(p?.colors),
    ...splitOptions(p?.color),
    ...splitOptions(p?.colorOptions),
    ...splitOptions(p?.availableColors),
    ...splitOptions(p?.variants?.colors),
    ...splitOptions(p?.variants?.color),
    ...splitOptions(p?.options?.colors),
    ...splitOptions(p?.options?.color),
    ...getAttrOptionsFromProduct(p, [
      "color",
      "colors",
      "colour",
      "colours",
      "Color",
      "Colors",
      "Colour",
      "Colours",
    ]),
  ];

  const seen = new Set();
  const final = [];
  for (const item of out) {
    const k = safe(item).toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    final.push(item);
  }
  return final;
}

/* ---------------- Swatch color support ---------------- */
const COLOR_MAP = {
  black: "#111827",
  white: "#ffffff",
  red: "#ef4444",
  blue: "#2563eb",
  green: "#16a34a",
  yellow: "#f59e0b",
  orange: "#f97316",
  purple: "#7c3aed",
  pink: "#ec4899",
  indigo: "#4f46e5",
  violet: "#8b5cf6",
  teal: "#14b8a6",
  cyan: "#06b6d4",
  lime: "#84cc16",
  emerald: "#10b981",
  amber: "#f59e0b",
  rose: "#f43f5e",
  maroon: "#7f1d1d",
  navy: "#0f172a",
  brown: "#92400e",
  beige: "#f5f5dc",
  cream: "#fffdd0",
  gold: "#d4af37",
  silver: "#cbd5e1",
  gray: "#6b7280",
  grey: "#6b7280",
  magenta: "#d946ef",
  fuchsia: "#d946ef",
  turquoise: "#06b6d4",
  azure: "#3b82f6",
  coral: "#fb7185",
  salmon: "#fb7185",
  peach: "#fdba74",
  mint: "#34d399",
  olive: "#65a30d",
  mustard: "#ca8a04",
  lavender: "#a78bfa",
  plum: "#7c3aed",
  chocolate: "#7c2d12",
  tan: "#d2b48c",
  khaki: "#b59f3b",
  charcoal: "#334155",
  slate: "#475569",

  // ✅ Fix aqua
  aqua: "#00ffff",
  "aqua blue": "#00ffff",
  aquamarine: "#7fffd4",
  "light aqua": "#b2ffff",
  "deep aqua": "#00c4c4",
};

function isHexColor(s) {
  const t = safe(s);
  return /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(t);
}
function isRgbColor(s) {
  const t = safe(s).toLowerCase();
  return t.startsWith("rgb(") || t.startsWith("rgba(");
}
function isHslColor(s) {
  const t = safe(s).toLowerCase();
  return t.startsWith("hsl(") || t.startsWith("hsla(");
}

function hashToHsl(str) {
  const s = safe(str).toLowerCase();
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return `hsl(${hue} 70% 50%)`;
}

function shadeAdjust(baseHex, mode) {
  try {
    const hex = baseHex.replace("#", "");
    const full =
      hex.length === 3
        ? hex
            .split("")
            .map((c) => c + c)
            .join("")
        : hex.slice(0, 6);

    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);

    const amt = mode === "light" ? 50 : mode === "dark" ? -50 : 0;
    const clamp = (n) => Math.max(0, Math.min(255, n));

    const rr = clamp(r + amt);
    const gg = clamp(g + amt);
    const bb = clamp(b + amt);

    const toHex = (n) => n.toString(16).padStart(2, "0");
    return `#${toHex(rr)}${toHex(gg)}${toHex(bb)}`;
  } catch {
    return baseHex;
  }
}

function normalizeColorToken(name) {
  const raw = safe(name);
  if (!raw) return "#94a3b8";

  if (isHexColor(raw)) return raw;
  if (isRgbColor(raw)) return raw;
  if (isHslColor(raw)) return raw;

  const cleaned = raw.toLowerCase().replace(/\s+/g, " ").trim();

  const light = cleaned.startsWith("light ");
  const dark = cleaned.startsWith("dark ");
  const baseName = cleaned.replace(/^light\s+|^dark\s+/, "").trim();

  if (COLOR_MAP[cleaned]) return COLOR_MAP[cleaned];

  const base =
    COLOR_MAP[baseName] ||
    Object.entries(COLOR_MAP).find(([k]) => baseName.includes(k))?.[1];

  if (base) {
    if (light) return shadeAdjust(base, "light");
    if (dark) return shadeAdjust(base, "dark");
    return base;
  }

  // ✅ any unknown name becomes a stable swatch
  return hashToHsl(cleaned);
}

/* ---------------- Mobile bottom-sheet size picker ---------------- */
function SizeSheet({ open, title, options, value, onPick, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="sheetBack" role="dialog" aria-modal="true">
      <button
        className="sheetOverlay"
        type="button"
        onClick={onClose}
        aria-label="Close"
      />
      <div className="sheet">
        <div className="sheetHead">
          <div className="sheetTitle">{title || "Select size"}</div>
          <button className="sheetClose" type="button" onClick={onClose}>
            Done
          </button>
        </div>

        <div className="sheetBody">
          <div className="sheetList">
            {(options || []).map((opt) => {
              const on = safe(opt).toLowerCase() === safe(value).toLowerCase();
              return (
                <button
                  key={opt}
                  type="button"
                  className={`sheetItem ${on ? "on" : ""}`}
                  onClick={() => {
                    onPick?.(opt);
                    onClose?.();
                  }}
                >
                  <span>{opt}</span>
                  {on ? <span className="sheetCheck">✓</span> : null}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PublicProductScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { productId: rawProductId } = useParams();
  const productId = useMemo(() => safe(rawProductId), [rawProductId]);

  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [product, setProduct] = useState(null);

  const storeId = safe(product?.storeId);
  const storeName = safe(product?.storeName);

  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    if (!storeId) return;
    setCartItems(loadCart(storeId));
  }, [storeId]);

  useEffect(() => {
    if (!storeId) return;
    const onStorage = (e) => {
      if (e?.key === cartKey(storeId)) setCartItems(loadCart(storeId));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storeId]);

  const qtyInCart = useMemo(
    () => getQtyInCart(cartItems, productId),
    [cartItems, productId]
  );

  const [qty, setQty] = useState(1);
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedSize, setSelectedSize] = useState("");

  const [related, setRelated] = useState([]);

  const images = useMemo(() => {
    if (!product) return [];
    const list = [];
    const a = safe(product.thumbnailUrl);
    const b = safe(product.imageUrl);
    if (a) list.push(a);
    if (b && b !== a) list.push(b);

    const arr = Array.isArray(product.mediaUrls) ? product.mediaUrls : [];
    for (const u of arr) {
      const s = safe(u);
      if (s && !list.includes(s)) list.push(s);
    }
    return list;
  }, [product]);

  const [activeImg, setActiveImg] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);

  const nextImg = () =>
    setActiveImg((i) => (images.length ? (i + 1) % images.length : 0));
  const prevImg = () =>
    setActiveImg((i) =>
      images.length ? (i - 1 + images.length) % images.length : 0
    );

  useEffect(() => {
    if (!viewerOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setViewerOpen(false);
      if (e.key === "ArrowRight") nextImg();
      if (e.key === "ArrowLeft") prevImg();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerOpen, images.length]);

  const attrs = useMemo(() => {
    const a = product?.attributes;
    if (!a || typeof a !== "object" || Array.isArray(a)) return {};
    return a;
  }, [product]);

  const colorOptions = useMemo(() => getColorsFromProduct(product), [product]);
  const sizeOptions = useMemo(() => getSizesFromProduct(product), [product]);

  const extraAttrs = useMemo(() => {
    const a = attrs || {};
    const skip = new Set([
      "color",
      "colors",
      "colour",
      "colours",
      "size",
      "sizes",
    ]);
    return Object.entries(a)
      .filter(([k, v]) => {
        const key = safe(k).toLowerCase();
        const val = safe(v);
        if (!key || !val) return false;
        if (skip.has(key)) return false;
        return true;
      })
      .slice(0, 30);
  }, [attrs]);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq1 = window.matchMedia?.("(max-width: 640px)");
    const mq2 = window.matchMedia?.("(pointer: coarse)");
    const compute = () => setIsMobile(Boolean(mq1?.matches || mq2?.matches));
    compute();
    mq1?.addEventListener?.("change", compute);
    mq2?.addEventListener?.("change", compute);
    return () => {
      mq1?.removeEventListener?.("change", compute);
      mq2?.removeEventListener?.("change", compute);
    };
  }, []);

  const [sizeSheetOpen, setSizeSheetOpen] = useState(false);
  const [publicStorePath, setPublicStorePath] = useState("/browse");

  useEffect(() => {
    let alive = true;

    async function resolveStorePath(p) {
      const storeId2 = safe(p?.storeId);
      const slugFromProduct = safe(p?.storeSlug);

      if (slugFromProduct) return `/s/${encodeURIComponent(slugFromProduct)}`;
      if (!storeId2) return "/browse";

      try {
        const storeSnap = await getDoc(doc(db, "stores", storeId2));
        const storeSlug = storeSnap.exists()
          ? safe(storeSnap.data()?.storeSlug)
          : "";
        if (storeSlug) return `/s/${encodeURIComponent(storeSlug)}`;
        return `/s/${encodeURIComponent(storeId2)}`;
      } catch {
        return `/s/${encodeURIComponent(storeId2)}`;
      }
    }

    (async () => {
      if (!product) return;
      const path = await resolveStorePath(product);
      if (!alive) return;
      setPublicStorePath(path);
    })();

    return () => {
      alive = false;
    };
  }, [product]);

  useEffect(() => {
    let alive = true;

    async function loadAll() {
      setLoading(true);
      setMsg("");
      setProduct(null);
      setRelated([]);

      if (!productId) {
        setMsg("Product id missing.");
        setLoading(false);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "products", productId));
        if (!snap.exists()) {
          setMsg("Product not found.");
          setLoading(false);
          return;
        }

        const data = { id: snap.id, ...(snap.data() || {}) };
        if (!alive) return;

        setProduct(data);
        setActiveImg(0);
        setQty(1);

        const colors = getColorsFromProduct(data);
        const sizes = getSizesFromProduct(data);

        setSelectedColor(colors[0] || "");
        setSelectedSize(sizes[0] || "");

        const storeId2 = safe(data?.storeId);
        const catId = safe(data?.categoryId);

        if (storeId2) {
          let relQ = null;

          if (catId) {
            relQ = query(
              collection(db, "products"),
              where("storeId", "==", storeId2),
              where("categoryId", "==", catId),
              limit(12)
            );
          } else {
            relQ = query(
              collection(db, "products"),
              where("storeId", "==", storeId2),
              limit(12)
            );
          }

          const relSnap = await getDocs(relQ);
          if (!alive) return;

          const list = relSnap.docs
            .map((d) => ({ id: d.id, ...(d.data() || {}) }))
            .filter((pp) => String(pp.id) !== String(productId))
            .slice(0, 8);

          setRelated(list);
        }

        setLoading(false);
      } catch (e) {
        setMsg(e?.message || "Failed to load product.");
        setLoading(false);
      }
    }

    loadAll();
    return () => {
      alive = false;
    };
  }, [productId]);

  const title = safe(product?.title) || "Product";
  const desc = safe(product?.description) || "No description yet.";

  const cur = safe(product?.currency) || "GHS";
  const basePriceVal = getPriceValue(product);
  const discount = Number(product?.discountPercent || 0);

  // ✅ if discount is on: YOUR "price" is the old price
  const hasDiscount =
    basePriceVal != null && Number.isFinite(discount) && discount > 0;

  const finalPriceVal =
    basePriceVal == null
      ? null
      : hasDiscount
      ? applyDiscount(basePriceVal, discount)
      : basePriceVal;

  const compareAt = hasDiscount ? basePriceVal : null;

  const showDiscount =
    hasDiscount &&
    compareAt != null &&
    finalPriceVal != null &&
    compareAt > finalPriceVal;

  const priceText =
    finalPriceVal == null ? "Contact for price" : money(finalPriceVal, cur);
  const compareText = compareAt == null ? "" : money(compareAt, cur);

  const cat = safe(product?.categoryName) || safe(product?.categoryId) || "—";
  const type = safe(product?.productType) || "—";

  const condition =
    safe(product?.condition) ||
    safe(product?.itemCondition) ||
    safe(
      getAttrCI(attrs, [
        "Condition",
        "condition",
        "Item Condition",
        "item condition",
      ])
    ) ||
    "—";

  const stockNum =
    product?.stock == null || product?.stock === "" ? null : Number(product?.stock);

  const stockLabel =
    stockNum == null || Number.isNaN(stockNum)
      ? "—"
      : stockNum <= 0
      ? "Out of stock"
      : `${stockNum} Left`;

  const setQtySafe = (next) => {
    const n = Number(next);
    if (Number.isNaN(n)) return;
    setQty(Math.max(1, Math.min(99, n)));
  };

  const addToCart = () => {
    const sid = safe(product?.storeId);
    if (!productId || !sid || !product) return;

    if (colorOptions.length && !safe(selectedColor)) {
      alert("Please select a color.");
      return;
    }
    if (sizeOptions.length && !safe(selectedSize)) {
      alert("Please select a size.");
      return;
    }

    const addQty = Math.max(1, Math.min(99, Number(qty || 1)));

    const img =
      safe(product?.thumbnailUrl) ||
      safe(product?.imageUrl) ||
      safe(product?.mediaUrls?.[0]) ||
      "";

    const unitPrice = finalPriceVal == null ? 0 : Number(finalPriceVal);

    setCartItems((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const pid = String(productId);

      const idx = next.findIndex((x) => String(x?.productId) === pid);

      const line = {
        lineId: `${pid}__single`,
        productId: pid,
        qty: addQty,

        title: safe(product?.title) || "Item",
        currency: safe(product?.currency) || "GHS",
        unitPrice,
        imageUrl: img,

        options: {
          color: safe(selectedColor),
          size: safe(selectedSize),
          variantId: "",
          variantTitle: "",
        },

        discountPercent: Number(product?.discountPercent || 0),
        compareAtPrice: compareAt || 0,

        storeId: safe(sid),
        storeName: safe(storeName),
        addedAt: Date.now(),
      };

      if (idx >= 0) next[idx] = { ...next[idx], ...line };
      else next.push(line);

      saveCart(sid, next);
      return next;
    });
  };

  const handleBuyNow = () => {
    if (!productId) return;

    if (colorOptions.length && !selectedColor) {
      alert("Please select a color.");
      return;
    }
    if (sizeOptions.length && !selectedSize) {
      alert("Please select a size.");
      return;
    }

    navigate("/checkout", {
      state: {
        productId,
        storeId: safe(product?.storeId),
        qty,
        selectedColor,
        selectedSize,
      },
    });
  };

  const relatedImage = (p) =>
    safe(p?.thumbnailUrl) || safe(p?.imageUrl) || safe((p?.mediaUrls || [])[0]);

  const backToStore = () => {
    const from = safe(location?.state?.fromStore);
    if (from) return navigate(from);
    return navigate(publicStorePath);
  };

  return (
    <div className="pd">
      <style>{css}</style>

      <div className="wrap">
        <div className="topRow">
          <div className="crumbs">
            <span className="crumb" onClick={backToStore}>
              Home
            </span>
            <span className="sep">›</span>

            <span className="crumb" onClick={backToStore}>
              {safe(product?.storeName) ||
                safe(product?.storeSlug) ||
                safe(product?.storeId) ||
                "Store"}
            </span>

            <span className="sep">›</span>
            <span className="crumb strong">{title}</span>
          </div>
        </div>

        {loading ? (
          <div className="card">Loading…</div>
        ) : msg ? (
          <div className="card err">{msg}</div>
        ) : !product ? (
          <div className="card err">Product missing.</div>
        ) : (
          <>
            <div className="grid">
              <div className="gallery">
                <div
                  className="bigImg"
                  onClick={() => images[activeImg] && setViewerOpen(true)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setViewerOpen(true);
                  }}
                  aria-label="Open image viewer"
                >
                  {images[activeImg] ? (
                    <img src={images[activeImg]} alt={title} />
                  ) : (
                    <div className="ph">📦</div>
                  )}
                  {showDiscount ? (
                    <div className="badgeOff">{discount}% OFF</div>
                  ) : null}
                </div>

                {images.length > 1 ? (
                  <div className="thumbRow">
                    {images.slice(0, 6).map((u, i) => (
                      <button
                        key={u}
                        className={`thumb ${i === activeImg ? "on" : ""}`}
                        type="button"
                        onClick={() => setActiveImg(i)}
                        title={`Image ${i + 1}`}
                      >
                        <img src={u} alt={`thumb-${i}`} />
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="panel">
                <div className="title">{title}</div>

                <div className="subLine">
                  <span className="dot" />
                  <span className="muted">{cat}</span>
                  <span className="pipe">|</span>
                  <span className="muted">{type}</span>
                </div>

                <div className="priceRow">
                  <div className="now">{priceText}</div>
                  {showDiscount ? (
                    <>
                      <div className="old">{compareText}</div>
                      <div className="miniOff">{discount}% OFF</div>
                    </>
                  ) : null}
                </div>

                <div className="shortDesc">{safe(product?.shortDescription) || " "}</div>

                <ul className="bullets">
                  <li>
                    <span className="bK">Type:</span>{" "}
                    <span className="bV">{type}</span>
                  </li>
                  <li>
                    <span className="bK">Category:</span>{" "}
                    <span className="bV">{cat}</span>
                  </li>
                  <li>
                    <span className="bK">Condition:</span>{" "}
                    <span className="bV">{condition}</span>
                  </li>
                  <li>
                    <span className="bK">Stock:</span>{" "}
                    <span className={`bV ${stockNum != null && stockNum <= 3 ? "warn" : ""}`}>
                      {stockLabel}
                    </span>
                  </li>
                </ul>

                {/* Color */}
                <div className="optRow">
                  <div className="optLabel">Color:</div>
                  {colorOptions.length ? (
                    <div className="swatches">
                      {colorOptions.slice(0, 60).map((c) => {
                        const isOn =
                          safe(selectedColor).toLowerCase() === safe(c).toLowerCase();
                        const bg = normalizeColorToken(c);
                        const isWhite = String(bg).toLowerCase() === "#ffffff";
                        return (
                          <button
                            key={c}
                            type="button"
                            className={`swatch ${isOn ? "on" : ""} ${isWhite ? "white" : ""}`}
                            onClick={() => setSelectedColor(c)}
                            title={c}
                          >
                            <span className="swIn" style={{ background: bg }} />
                          </button>
                        );
                      })}
                      <span className="pickName">{selectedColor}</span>
                    </div>
                  ) : (
                    <div className="muted">—</div>
                  )}
                </div>

                {/* Size */}
                <div className="optRow">
                  <div className="optLabel">Size:</div>
                  {sizeOptions.length ? (
                    isMobile ? (
                      <>
                        <button
                          type="button"
                          className="pickBtn"
                          onClick={() => setSizeSheetOpen(true)}
                        >
                          <span className="pickBtnTxt">{selectedSize || "Select size"}</span>
                          <span className="pickBtnArrow">▾</span>
                        </button>

                        <SizeSheet
                          open={sizeSheetOpen}
                          title="Select size"
                          options={sizeOptions}
                          value={selectedSize}
                          onPick={(v) => setSelectedSize(v)}
                          onClose={() => setSizeSheetOpen(false)}
                        />
                      </>
                    ) : (
                      <select
                        className="select"
                        value={selectedSize}
                        onChange={(e) => setSelectedSize(e.target.value)}
                      >
                        {sizeOptions.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    )
                  ) : (
                    <div className="muted">—</div>
                  )}
                </div>

                {/* Extra Details */}
                {extraAttrs.length ? (
                  <div className="extraPanel">
                    <div className="extraTitle">Extra Details</div>
                    <div className="extraBox">
                      <div className="attrGrid">
                        {extraAttrs.map(([k, v]) => (
                          <div key={String(k)} className="attrRow">
                            <div className="attrK">{safe(k)}</div>
                            <div className="attrV">{safe(v)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Quantity */}
                <div className="optRow">
                  <div className="optLabel">Quantity:</div>
                  <div className="qty">
                    <button type="button" onClick={() => setQtySafe(qty - 1)}>
                      −
                    </button>
                    <div className="qNum">{qty}</div>
                    <button type="button" onClick={() => setQtySafe(qty + 1)}>
                      +
                    </button>
                  </div>
                </div>

                <div className="btnRow">
                  <button className="btnCart" type="button" onClick={addToCart}>
                    ADD TO CART {qtyInCart > 0 ? `(${qtyInCart})` : ""}
                  </button>
                  <button className="btnBuy" type="button" onClick={handleBuyNow}>
                    Buy Now →
                  </button>
                </div>

                {qtyInCart > 0 ? (
                  <div style={{ marginTop: 10, color: "#64748b", fontWeight: 900, fontSize: 12 }}>
                    Saved in cart as ONE item. Adding again updates qty/options.
                  </div>
                ) : null}
              </div>
            </div>

            <div className="section">
              <div className="h2">Product Description</div>
              <div className="box">{desc}</div>
            </div>

            <div className="section">
              <div className="h2">Related Items</div>
              {related.length ? (
                <div className="relGrid">
                  {related.map((p) => {
                    const img = relatedImage(p);
                    const pTitle = safe(p?.title) || "Product";
                    const pCur = safe(p?.currency) || cur;

                    const pBase = getPriceValue(p);
                    const pDisc = Number(p?.discountPercent || 0);
                    const pHas = pBase != null && Number.isFinite(pDisc) && pDisc > 0;
                    const pFinal = pBase == null ? null : pHas ? applyDiscount(pBase, pDisc) : pBase;

                    const pPrice = pFinal == null ? "—" : money(pFinal, pCur);

                    return (
                      <button
                        key={p.id}
                        className="relCard"
                        type="button"
                        onClick={() =>
                          navigate(`/p/${p.id}`, { state: { fromStore: publicStorePath } })
                        }
                        title={pTitle}
                      >
                        <div className="relImg">
                          {img ? <img src={img} alt={pTitle} /> : <div className="ph2">🛍️</div>}
                        </div>
                        <div className="relBody">
                          <div className="relTitle">{pTitle}</div>
                          <div className="relPrice">{pPrice}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="box mutedBox">No related items yet.</div>
              )}
            </div>
          </>
        )}

        <button className="fabBack" type="button" onClick={() => navigate(-1)}>
          ←
        </button>
      </div>

      {/* Viewer */}
      {viewerOpen ? (
        <div className="viewerBack" onClick={() => setViewerOpen(false)}>
          <div className="viewer" onClick={(e) => e.stopPropagation()}>
            <button
              className="viewerClose"
              type="button"
              onClick={() => setViewerOpen(false)}
              aria-label="Close"
            >
              ✕
            </button>

            {images.length > 1 ? (
              <>
                <button
                  className="viewerNav left"
                  type="button"
                  onClick={prevImg}
                  aria-label="Previous"
                >
                  ‹
                </button>
                <button
                  className="viewerNav right"
                  type="button"
                  onClick={nextImg}
                  aria-label="Next"
                >
                  ›
                </button>
              </>
            ) : null}

            <img className="viewerImg" src={images[activeImg]} alt={title} />
            <a
              className="viewerOpen"
              href={images[activeImg]}
              target="_blank"
              rel="noreferrer"
            >
              Open in new tab
            </a>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ✅ FULL CSS */
const css = `
:root{
  --bg:#f5f6fa;
  --card:#ffffff;
  --ink:#0f172a;
  --muted:#64748b;
  --line:#e6e9ef;
  --red:#ef4444;
  --red2:#dc2626;
  --shadow:0 12px 30px rgba(15,23,42,.10);
}
*{box-sizing:border-box}
.pd{min-height:100vh;background:var(--bg);padding:18px 12px;font-family:Inter,system-ui,Segoe UI,Arial;color:var(--ink)}
.wrap{max-width:1100px;margin:0 auto;position:relative}

.topRow{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:12px}
.crumbs{display:flex;align-items:center;gap:8px;color:var(--muted);font-weight:800;cursor:pointer;user-select:none;flex-wrap:wrap}
.crumb{font-weight:800}
.crumb.strong{color:var(--ink)}
.sep{opacity:.6}

.card{background:var(--card);border:1px solid var(--line);border-radius:16px;padding:14px;box-shadow:var(--shadow)}
.card.err{border-color:#fecaca;color:#991b1b}

.grid{display:grid;grid-template-columns:1.1fr .9fr;gap:16px;align-items:start}
@media (max-width: 900px){ .grid{grid-template-columns:1fr} }

.gallery{background:var(--card);border:1px solid var(--line);border-radius:18px;box-shadow:var(--shadow);padding:14px}
.bigImg{
  position:relative;border-radius:14px;overflow:hidden;background:#f1f5f9;
  aspect-ratio:1/1;display:grid;place-items:center;
  cursor:pointer;
}
.bigImg img{width:100%;height:100%;object-fit:cover}
.ph{font-size:64px;opacity:.7}

.badgeOff{
  position:absolute;left:12px;top:12px;
  background:linear-gradient(135deg,var(--red2),var(--red));
  color:#fff;font-weight:1100;font-size:12px;
  padding:8px 12px;border-radius:12px;
  box-shadow:0 10px 20px rgba(239,68,68,.25);
}

.thumbRow{display:flex;gap:10px;margin-top:12px;flex-wrap:wrap}
.thumb{
  border:1px solid var(--line);background:#fff;border-radius:12px;
  overflow:hidden;width:72px;height:72px;cursor:pointer;padding:0;
}
.thumb.on{border-color:#fca5a5;box-shadow:0 10px 18px rgba(239,68,68,.18)}
.thumb img{width:100%;height:100%;object-fit:cover}

.panel{background:var(--card);border:1px solid var(--line);border-radius:18px;box-shadow:var(--shadow);padding:16px}
.title{font-size:34px;font-weight:1100;letter-spacing:-.02em}
@media (max-width: 900px){ .title{font-size:28px} }

.subLine{margin-top:6px;display:flex;align-items:center;gap:10px;color:var(--muted);font-weight:900}
.dot{width:10px;height:10px;border-radius:999px;background:#cbd5e1;display:inline-block}
.pipe{opacity:.5}
.muted{color:var(--muted)}

.priceRow{margin-top:14px;display:flex;align-items:center;gap:12px;flex-wrap:wrap}
.now{font-size:26px;font-weight:1100}
.old{color:#94a3b8;text-decoration:line-through;font-weight:1000}
.miniOff{
  background:#fee2e2;border:1px solid #fecaca;color:#991b1b;
  padding:6px 10px;border-radius:10px;font-weight:1100;font-size:12px;
}

.shortDesc{margin-top:10px;min-height:8px}

.bullets{margin:12px 0 0;padding-left:18px;color:var(--ink);font-weight:900;line-height:1.8}
.bK{color:#475569;font-weight:1100;margin-right:6px}
.bV{font-weight:1100}
.bV.warn{color:var(--red2)}

.optRow{margin-top:14px;display:flex;align-items:center;gap:12px;flex-wrap:wrap}
.optLabel{min-width:90px;color:#334155;font-weight:1100}

.swatches{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.swatch{
  border:1px solid var(--line);background:#fff;border-radius:10px;
  padding:6px;cursor:pointer;
}
.swatch.on{border-color:#fca5a5;box-shadow:0 10px 16px rgba(239,68,68,.15)}
.swatch.white{border-color:#cbd5e1}
.swIn{width:22px;height:22px;border-radius:8px;display:block;border:1px solid rgba(15,23,42,.10)}
.pickName{color:#475569;font-weight:1000;margin-left:6px}

.select{
  border:1px solid var(--line);background:#fff;border-radius:10px;
  padding:10px 12px;font-weight:1000;min-width:140px;
}

.pickBtn{
  border:1px solid var(--line);
  background:#fff;
  border-radius:10px;
  padding:10px 12px;
  min-width:170px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  font-weight:1100;
  cursor:pointer;
}
.pickBtnTxt{color:#0f172a}
.pickBtnArrow{opacity:.7}

.qty{display:flex;align-items:center;border:1px solid var(--line);border-radius:10px;overflow:hidden;background:#fff}
.qty button{width:42px;height:40px;border:0;background:#fff;cursor:pointer;font-size:18px;font-weight:1100}
.qNum{width:52px;text-align:center;font-weight:1100;color:#0f172a}

.btnRow{margin-top:16px;display:flex;gap:12px;flex-wrap:wrap}
.btnCart{
  flex:1;
  border:0;border-radius:12px;padding:14px 16px;
  background:linear-gradient(135deg,var(--red2),var(--red));
  color:#fff;font-weight:1100;cursor:pointer;
  box-shadow:0 14px 24px rgba(239,68,68,.22);
}
.btnBuy{
  flex:1;
  border:1px solid var(--line);border-radius:12px;padding:14px 16px;
  background:#fff;color:#0f172a;font-weight:1100;cursor:pointer;
}

.section{margin-top:16px}
.h2{font-size:16px;font-weight:1100;margin:0 0 10px}
.box{
  background:#fff;border:1px solid var(--line);border-radius:14px;
  padding:14px;box-shadow:var(--shadow);color:#0b1220;font-weight:700;line-height:1.5
}
.mutedBox{color:var(--muted);font-weight:900}

.extraPanel{margin-top:14px}
.extraTitle{font-weight:1200;color:#334155;margin-bottom:8px}
.extraBox{
  border:1px solid var(--line);
  border-radius:14px;
  padding:12px;
  background:#fff;
}
.attrGrid{display:grid;gap:10px}
.attrRow{
  display:grid;
  grid-template-columns: 160px 1fr;
  gap:10px;
  padding:10px 0;
  border-bottom:1px dashed var(--line);
}
.attrRow:last-child{border-bottom:0}
.attrK{color:#475569;font-weight:1100}
.attrV{color:#0f172a;font-weight:1000;word-break:break-word}
@media (max-width: 640px){
  .attrRow{grid-template-columns: 1fr}
  .attrK{font-size:12px}
}

.relGrid{
  display:grid;
  grid-template-columns:repeat(4,1fr);
  gap:12px;
}
@media (max-width: 900px){ .relGrid{grid-template-columns:repeat(2,1fr)} }

.relCard{
  border:1px solid var(--line);
  background:#fff;
  border-radius:16px;
  overflow:hidden;
  padding:0;
  cursor:pointer;
  box-shadow:var(--shadow);
  text-align:left;
}
.relImg{aspect-ratio:1/1;background:#f1f5f9;display:grid;place-items:center}
.relImg img{width:100%;height:100%;object-fit:cover}
.ph2{font-size:40px;opacity:.75}
.relBody{padding:10px}
.relTitle{font-weight:1100;line-height:1.2;max-height:2.4em;overflow:hidden}
.relPrice{margin-top:6px;color:#0f172a;font-weight:1100}

.fabBack{
  position:fixed;left:14px;bottom:14px;
  width:46px;height:46px;border-radius:14px;
  border:1px solid var(--line);background:#fff;
  box-shadow:0 14px 24px rgba(15,23,42,.14);
  font-weight:1100;cursor:pointer;
}
@media (min-width: 900px){
  .fabBack{display:none}
}

.sheetBack{
  position:fixed;
  inset:0;
  z-index:9999;
  display:grid;
  align-items:end;
}
.sheetOverlay{
  position:absolute;
  inset:0;
  border:0;
  background:rgba(15,23,42,.45);
  cursor:pointer;
}
.sheet{
  position:relative;
  width:100%;
  background:#fff;
  border-top-left-radius:18px;
  border-top-right-radius:18px;
  box-shadow:0 -18px 40px rgba(15,23,42,.22);
  padding:12px;
}
.sheetHead{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding:6px 4px 10px;
}
.sheetTitle{
  font-weight:1200;
  font-size:16px;
  color:#0f172a;
}
.sheetClose{
  border:1px solid var(--line);
  background:#fff;
  border-radius:12px;
  padding:8px 12px;
  font-weight:1100;
  cursor:pointer;
}
.sheetBody{
  max-height:70vh;
  overflow:auto;
  padding-bottom:8px;
}
.sheetList{
  display:flex;
  flex-direction:column;
  gap:10px;
}
.sheetItem{
  width:100%;
  text-align:left;
  border:1px solid var(--line);
  background:#fff;
  border-radius:14px;
  padding:12px 12px;
  font-weight:1100;
  cursor:pointer;
  display:flex;
  align-items:center;
  justify-content:space-between;
}
.sheetItem.on{
  border-color:#fca5a5;
  box-shadow:0 10px 16px rgba(239,68,68,.12);
}
.sheetCheck{
  color:#16a34a;
  font-weight:1300;
}

/* Viewer */
.pd .viewerBack{
  position:fixed !important;
  inset:0 !important;
  z-index:999999 !important;
  background:rgba(15,23,42,.75) !important;
  display:grid !important;
  place-items:center !important;
  padding:16px !important;
}
.pd .viewer{
  position:relative !important;
  width:min(980px, 96vw) !important;
  height:min(720px, 90vh) !important;
  background:#0b1220 !important;
  border-radius:18px !important;
  overflow:hidden !important;
  box-shadow:0 20px 70px rgba(0,0,0,.35) !important;
  display:grid !important;
  grid-template-rows: 1fr auto !important;
}
.pd .viewerImg{
  width:100% !important;
  height:100% !important;
  object-fit:contain !important;
  background:#0b1220 !important;
  display:block !important;
}
.pd .viewerClose{
  position:absolute !important;
  top:10px !important;
  right:10px !important;
  border:0 !important;
  background:rgba(255,255,255,.12) !important;
  color:#fff !important;
  width:42px !important;
  height:42px !important;
  border-radius:14px !important;
  cursor:pointer !important;
  font-weight:1100 !important;
  font-size:18px !important;
}
.pd .viewerNav{
  position:absolute !important;
  top:50% !important;
  transform:translateY(-50%) !important;
  border:0 !important;
  background:rgba(255,255,255,.12) !important;
  color:#fff !important;
  width:48px !important;
  height:56px !important;
  border-radius:16px !important;
  cursor:pointer !important;
  font-weight:1200 !important;
  font-size:28px !important;
}
.pd .viewerNav.left{left:10px !important}
.pd .viewerNav.right{right:10px !important}
.pd .viewerOpen{
  padding:10px 12px !important;
  text-align:center !important;
  color:#fff !important;
  font-weight:1000 !important;
  text-decoration:none !important;
  border-top:1px solid rgba(255,255,255,.12) !important;
  background:rgba(255,255,255,.06) !important;
}
`;
